(() => {
  if (window.__bmhSunrisePremiumLoaded) return;
  window.__bmhSunrisePremiumLoaded = true;

  const SELECTOR = '[data-bmh-sunrise-page]';

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    return minutes + ':' + String(Math.floor(seconds % 60)).padStart(2, '0');
  };

  const timecodeToSeconds = (value = '0:00') => {
    const parts = value.split(':').map(Number).filter(Number.isFinite);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return parts[0] || 0;
  };

  const youtubeId = (rawUrl) => {
    try {
      const url = new URL(rawUrl);
      if (url.hostname.includes('youtu.be')) return url.pathname.split('/').filter(Boolean)[0] || '';
      if (url.pathname.includes('/shorts/')) return url.pathname.split('/shorts/')[1]?.split('/')[0] || '';
      if (url.pathname.includes('/embed/')) return url.pathname.split('/embed/')[1]?.split('/')[0] || '';
      return url.searchParams.get('v') || '';
    } catch {
      return '';
    }
  };

  const initHeroGallery = (hero) => {
    const gallery = hero.querySelector('.product-information__media');
    if (!gallery || gallery.dataset.bmhGalleryInitialized === 'true') return;
    gallery.dataset.bmhGalleryInitialized = 'true';

    const slideshow = gallery.querySelector('slideshow-component') || gallery;
    gallery.classList.add('bmh-product-gallery');
    slideshow.classList.add('bmh-product-gallery__slideshow');
    let slides = [...slideshow.querySelectorAll('slideshow-slide')];

    const refreshSlides = () => {
      slides = [...slideshow.querySelectorAll('slideshow-slide')];
    };
    const pauseHiddenVideos = () => {
      slides.forEach((slide) => {
        if (slide.getAttribute('aria-hidden') === 'true') {
          slide.querySelectorAll('video').forEach((video) => video.pause());
        }
      });
    };

    const playActiveVideoOnce = () => {
      pauseHiddenVideos();
      const active = slides.find((slide) => slide.getAttribute('aria-hidden') !== 'true');
      if (!active || active.dataset.bmhVideoPlayed === 'true' || !active.querySelector('deferred-media, video')) return;
      active.dataset.bmhVideoPlayed = 'true';
      const poster = active.querySelector('deferred-media button, .deferred-media__poster-button');
      poster?.click();
      window.setTimeout(() => {
        const video = active.querySelector('video');
        if (!video) return;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = false;
        video.play().catch(() => {});
      }, 80);
    };

    if (slides.length) {
      const observer = new MutationObserver(() => {
        refreshSlides();
        playActiveVideoOnce();
      });
      observer.observe(slideshow, { attributes: true, subtree: true, attributeFilter: ['aria-hidden'] });
      playActiveVideoOnce();
    }
  };

  const initHeroZoom = (hero) => {
    if (hero.dataset.bmhZoomInitialized === 'true') return;
    hero.dataset.bmhZoomInitialized = 'true';
    const gallery = hero.querySelector('.product-information__media');
    if (!gallery) return;

    const zoomableSlides = [...gallery.querySelectorAll('slideshow-slide.product-media-container--zoomable')];
    zoomableSlides.forEach((slide, index) => {
      if (slide.dataset.bmhZoomAccessible === 'true') return;
      slide.dataset.bmhZoomAccessible = 'true';
      const image = slide.querySelector('img');
      const description = image?.alt?.trim() || 'imagen del producto';
      slide.tabIndex = 0;
      slide.setAttribute('role', 'button');
      slide.setAttribute('aria-label', `Ampliar imagen ${index + 1}: ${description}`);
      slide.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        slide.click();
      });
    });

    const desktopZoom = window.matchMedia('(min-width: 750px) and (any-pointer: fine)');
    const zoomDialog = document.querySelector('zoom-dialog');
    if (!zoomDialog) return;

    const setupDesktopWrapper = (wrapper) => {
      if (wrapper.dataset.bmhDesktopZoom === 'true') return;
      wrapper.dataset.bmhDesktopZoom = 'true';
      wrapper.dataset.bmhZoomed = 'false';
      wrapper.dataset.bmhDragging = 'false';
      wrapper.tabIndex = 0;
      wrapper.setAttribute('aria-label', 'Imagen ampliada. Usá la rueda o doble clic para acercar y arrastrá para recorrerla.');

      const mediaContainer = wrapper.closest('.product-media-container--image');
      const originalCloseAction = mediaContainer?.getAttribute('on:click') || '';
      const syncCloseAction = () => {
        if (!mediaContainer || !originalCloseAction) return;
        if (desktopZoom.matches) mediaContainer.removeAttribute('on:click');
        else mediaContainer.setAttribute('on:click', originalCloseAction);
      };
      syncCloseAction();
      desktopZoom.addEventListener?.('change', syncCloseAction);

      const image = wrapper.querySelector('img');
      if (!image) return;
      const MIN_SCALE = 1;
      const MAX_SCALE = 4;
      let scale = MIN_SCALE;
      let translateX = 0;
      let translateY = 0;
      let dragging = false;
      let pointerId = null;
      let startX = 0;
      let startY = 0;
      let startTranslateX = 0;
      let startTranslateY = 0;

      const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

      const constrain = () => {
        const styles = getComputedStyle(wrapper);
        const width = Math.max(1, wrapper.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight));
        const height = Math.max(1, wrapper.clientHeight - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom));
        const naturalWidth = image.naturalWidth || width;
        const naturalHeight = image.naturalHeight || height;
        const imageRatio = naturalWidth / naturalHeight;
        const wrapperRatio = width / height;
        const fittedWidth = imageRatio > wrapperRatio ? width : height * imageRatio;
        const fittedHeight = imageRatio > wrapperRatio ? width / imageRatio : height;
        const maxX = Math.max(0, (fittedWidth * scale - width) / 2 / scale);
        const maxY = Math.max(0, (fittedHeight * scale - height) / 2 / scale);
        translateX = clamp(translateX, -maxX, maxX);
        translateY = clamp(translateY, -maxY, maxY);
      };

      const render = () => {
        constrain();
        wrapper.style.setProperty('--drag-zoom-scale', scale.toFixed(3));
        wrapper.style.setProperty('--drag-zoom-translate-x', `${translateX.toFixed(2)}px`);
        wrapper.style.setProperty('--drag-zoom-translate-y', `${translateY.toFixed(2)}px`);
        wrapper.dataset.bmhZoomed = String(scale > MIN_SCALE + .01);
        wrapper.dataset.bmhDragging = String(dragging);
      };

      const reset = () => {
        scale = MIN_SCALE;
        translateX = 0;
        translateY = 0;
        dragging = false;
        pointerId = null;
        render();
      };

      const zoomAt = (nextScale, clientX, clientY) => {
        const previousScale = scale;
        scale = clamp(nextScale, MIN_SCALE, MAX_SCALE);
        if (scale <= MIN_SCALE + .01) {
          translateX = 0;
          translateY = 0;
          render();
          return;
        }
        if (Number.isFinite(clientX) && Number.isFinite(clientY)) {
          const rect = wrapper.getBoundingClientRect();
          const distanceX = clientX - (rect.left + rect.width / 2);
          const distanceY = clientY - (rect.top + rect.height / 2);
          const scaleDelta = scale / previousScale - 1;
          translateX -= distanceX * scaleDelta / scale;
          translateY -= distanceY * scaleDelta / scale;
        }
        render();
      };

      wrapper.addEventListener('click', (event) => {
        if (desktopZoom.matches) event.stopPropagation();
      });

      wrapper.addEventListener('wheel', (event) => {
        if (!desktopZoom.matches) return;
        event.preventDefault();
        event.stopPropagation();
        const factor = Math.exp(-event.deltaY * .0015);
        zoomAt(scale * factor, event.clientX, event.clientY);
      }, { passive: false });

      wrapper.addEventListener('dblclick', (event) => {
        if (!desktopZoom.matches) return;
        event.preventDefault();
        event.stopPropagation();
        zoomAt(scale > 1.05 ? MIN_SCALE : 2.2, event.clientX, event.clientY);
      });

      wrapper.addEventListener('pointerdown', (event) => {
        if (!desktopZoom.matches || scale <= MIN_SCALE + .01 || event.button !== 0) return;
        event.preventDefault();
        event.stopPropagation();
        dragging = true;
        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        startTranslateX = translateX;
        startTranslateY = translateY;
        wrapper.setPointerCapture?.(pointerId);
        render();
      });

      wrapper.addEventListener('pointermove', (event) => {
        if (!dragging || event.pointerId !== pointerId) return;
        event.preventDefault();
        translateX = startTranslateX + (event.clientX - startX) / scale;
        translateY = startTranslateY + (event.clientY - startY) / scale;
        render();
      });

      const stopDragging = (event) => {
        if (!dragging || (event.pointerId != null && event.pointerId !== pointerId)) return;
        dragging = false;
        if (pointerId != null) wrapper.releasePointerCapture?.(pointerId);
        pointerId = null;
        render();
      };
      wrapper.addEventListener('pointerup', stopDragging);
      wrapper.addEventListener('pointercancel', stopDragging);

      wrapper.addEventListener('keydown', (event) => {
        if (!desktopZoom.matches) return;
        const rect = wrapper.getBoundingClientRect();
        if (event.key === '+' || event.key === '=') zoomAt(scale * 1.25, rect.left + rect.width / 2, rect.top + rect.height / 2);
        else if (event.key === '-') zoomAt(scale / 1.25, rect.left + rect.width / 2, rect.top + rect.height / 2);
        else if (event.key === '0') reset();
        else if (event.key === 'ArrowLeft') translateX += 36 / scale;
        else if (event.key === 'ArrowRight') translateX -= 36 / scale;
        else if (event.key === 'ArrowUp') translateY += 36 / scale;
        else if (event.key === 'ArrowDown') translateY -= 36 / scale;
        else return;
        event.preventDefault();
        render();
      });

      zoomDialog.querySelector('dialog')?.addEventListener('close', reset);
      render();
    };

    zoomDialog.querySelectorAll('drag-zoom-wrapper').forEach(setupDesktopWrapper);
    new MutationObserver(() => {
      zoomDialog.querySelectorAll('drag-zoom-wrapper').forEach(setupDesktopWrapper);
    }).observe(zoomDialog, { childList: true, subtree: true });
  };

  const initPrebuy = (hero) => {
    const prebuy = hero.querySelector('.bmh-prebuy');
    if (!prebuy || prebuy.dataset.bmhInitialized === 'true') return;
    prebuy.dataset.bmhInitialized = 'true';
    const items = [...prebuy.querySelectorAll('details')];
    items.forEach((item) => {
      item.removeAttribute('open');
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        items.forEach((other) => {
          if (other !== item) other.removeAttribute('open');
        });
      });
    });
  };

  const initHeroCart = (hero) => {
    if (hero.dataset.bmhCartInitialized === 'true') return;
    hero.dataset.bmhCartInitialized = 'true';
    const nativeAdd = hero.querySelector('product-form-component button[name="add"], product-form-component [ref="addToCartButton"]');
    const premiumAdd = hero.querySelector('.ai-premium-purchase__add-to-cart');
    if (!nativeAdd) return;
    let pendingOpen = false;

    const drawerIsOpen = () => Boolean(document.querySelector('cart-drawer-component dialog[open], cart-drawer-component [open]'));

    const openDrawer = () => {
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          if (drawerIsOpen()) {
            pendingOpen = false;
            return;
          }
          const drawer = document.querySelector('cart-drawer-component');
          if (drawer && typeof drawer.open === 'function') drawer.open();
          if (!drawerIsOpen() && drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
          if (!drawerIsOpen()) {
            const dialog = drawer?.querySelector('dialog');
            if (dialog && !dialog.open && typeof dialog.showModal === 'function') dialog.showModal();
          }
          if (!drawerIsOpen()) {
            const cartButton = [...document.querySelectorAll('button[aria-label="Carrito"], button[aria-label*="Carrito"]')]
              .find((button) => button.getClientRects().length && !button.disabled);
            cartButton?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, composed: true }));
          }
          pendingOpen = false;
        }, 140);
      });
    };

    const prepareOpen = () => {
      if (pendingOpen) return;
      pendingOpen = true;
      document.addEventListener('cart:update', openDrawer, { once: true });
      window.setTimeout(() => {
        if (!drawerIsOpen()) openDrawer();
      }, 950);
      window.setTimeout(() => {
        if (!drawerIsOpen()) openDrawer();
      }, 1900);
    };

    nativeAdd.addEventListener('click', prepareOpen);
    premiumAdd?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (nativeAdd.disabled || premiumAdd.getAttribute('aria-disabled') === 'true') return;
      nativeAdd.click();
    }, true);
  };

  const initHero = (scope = document) => {
    const selector = '#MainContent .product-information';
    const hero = scope.matches?.(selector) ? scope : scope.querySelector?.(selector);
    if (!hero) return;
    initHeroGallery(hero);
    initHeroZoom(hero);
    if (hero.closest('#MainContent')?.dataset.template === 'product.oferta-limitada') {
      initPrebuy(hero);
      initHeroCart(hero);
    }
  };

  const initReveal = (root) => {
    const items = root.querySelectorAll('[data-bmh-reveal]');
    if (root.closest('#MainContent[data-template^="product.oferta-limitada"]')) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((item) => observer.observe(item));
  };

  const initDawnStory = (root) => {
    const dawn = root.matches?.('[data-bmh-dawn]')
      ? root
      : root.querySelector('[data-bmh-dawn]');

    if (!dawn || dawn.dataset.bmhDawnInitialized === 'true') return;

    dawn.dataset.bmhDawnInitialized = 'true';

    const stage = dawn.querySelector('[data-dawn-stage]');
    const videos = [...dawn.querySelectorAll('.bmh-dawn__video')];
    const status = dawn.querySelector('[data-dawn-status]');

    const phrases = [
      stage?.getAttribute('data-phrase-1'),
      stage?.getAttribute('data-phrase-2'),
      stage?.getAttribute('data-phrase-3')
    ].filter(Boolean);

    const replay = dawn.querySelector('[data-dawn-replay]');
    const replayLabel = replay?.querySelector('[data-dawn-replay-label]');

    const autoplay = dawn.dataset.autoplay === 'true';

    const threshold = Math.min(
      .8,
      Math.max(
        .3,
        Number(dawn.dataset.visibleThreshold) || .55
      )
    );

    const fallbackDuration =
      Math.max(
        5,
        Number(dawn.dataset.fallbackDuration) || 8
      ) * 1000;

    const reduceMotion =
      matchMedia('(prefers-reduced-motion: reduce)').matches;

    let timers = [];

    let activeVideo = null;
    let activeStep = -1;

    let progressFrame = 0;
    let progressFrameSource = null;

    let fallbackFrame = 0;
    let phraseFrame = 0;

    let lastProgressPaint = 0;
    let lastProgressValue = -1;

    let started = false;
    let completed = false;

    let isInViewport = false;
    let reachedStartThreshold = false;

    const currentVideo = () =>
      videos.find(
        (video) => getComputedStyle(video).display !== 'none'
      ) ||
      videos[0] ||
      null;

    const setReplayLabel = (mode) => {
      if (!replayLabel || !replay) return;

      replayLabel.textContent =
        mode === 'replay'
          ? (
              replay.dataset.replayLabel ||
              'Repetir'
            )
          : (
              replay.dataset.playLabel ||
              'Ver la secuencia'
            );
    };

    const setStep = (index, instant = false) => {
      if (!phrases.length) return;

      const safeIndex = Math.min(
        phrases.length - 1,
        Math.max(0, index)
      );

      if (
        safeIndex === activeStep &&
        !instant
      ) {
        return;
      }

      activeStep = safeIndex;

      dawn.dataset.dawnStep =
        String(safeIndex + 1);

      if (!status) return;

      const nextPhrase =
        phrases[safeIndex] || '';

      /*
        Antes:
        texto actual -> opacity 0 + blur ->
        cambia texto -> vuelve a aparecer.

        Ahora:
        cambiamos el contenido directamente
        y solo hacemos un asentamiento sutil.
        Nunca desaparece por completo.
      */
      status.classList.remove(
        'is-leaving',
        'is-entering'
      );

      status.textContent = nextPhrase;

      if (
        !instant &&
        !reduceMotion
      ) {
        if (phraseFrame) {
          cancelAnimationFrame(phraseFrame);
        }

        phraseFrame =
          requestAnimationFrame(() => {
            status.classList.add('is-entering');

            phraseFrame = 0;
          });
      }
    };

    const setProgress = (
      value,
      instant = false
    ) => {
      const progress = Math.min(
        1,
        Math.max(
          0,
          Number(value) || 0
        )
      );

      /*
        No hace falta escribir una CSS variable
        literalmente 60 veces por segundo.

        Esto baja muchísimo el trabajo del main thread
        sin afectar visualmente la animación.
      */
      const shouldPaint =
        instant ||
        progress === 0 ||
        progress === 1 ||
        Math.abs(
          progress - lastProgressValue
        ) >= .004;

      if (shouldPaint) {
        dawn.style.setProperty(
          '--dawn-progress',
          progress.toFixed(4)
        );

        lastProgressValue = progress;
      }

      if (progress >= .66) {
        setStep(2, instant);
      } else if (progress >= .33) {
        setStep(1, instant);
      } else {
        setStep(0, instant);
      }
    };

    const clearTimers = () => {
      timers.forEach(
        window.clearTimeout
      );

      timers = [];

      if (fallbackFrame) {
        cancelAnimationFrame(
          fallbackFrame
        );
      }

      fallbackFrame = 0;
    };

    const stopProgressTracking = () => {
      if (progressFrame) {
        cancelAnimationFrame(
          progressFrame
        );
      }

      progressFrame = 0;
      progressFrameSource = null;
      lastProgressPaint = 0;
    };

    const trackVideoProgress = (video) => {
      stopProgressTracking();

      if (!video) return;

      progressFrameSource = video;

      const tick = (now) => {
        if (
          video !== activeVideo ||
          video !== progressFrameSource ||
          video.paused ||
          video.ended
        ) {
          progressFrame = 0;
          return;
        }

        /*
          ~15 actualizaciones por segundo.
          Suficiente para una barra de progreso
          y muchísimo más liviano que actualizar
          cada frame del video.
        */
        if (
          now - lastProgressPaint >= 64 &&
          Number.isFinite(video.duration) &&
          video.duration > 0
        ) {
          lastProgressPaint = now;

          setProgress(
            video.currentTime /
            video.duration
          );
        }

        progressFrame =
          requestAnimationFrame(tick);
      };

      progressFrame =
        requestAnimationFrame(tick);
    };

    const prewarmVideo = (
      video = currentVideo()
    ) => {
      if (
        !video ||
        video.dataset.bmhDawnPrewarmed === 'true'
      ) {
        return;
      }

      /*
        Solo preparamos el video que corresponde
        al breakpoint actual.

        Así evitamos hacer competir innecesariamente
        el vertical y el horizontal.
      */
      videos.forEach((candidate) => {
        if (
          candidate !== video &&
          !started
        ) {
          candidate.preload = 'none';
        }
      });

      video.dataset.bmhDawnPrewarmed =
        'true';

      video.preload = 'auto';

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      /*
        Cuando el bloque todavía está lejos,
        obligamos al navegador a preparar metadata,
        primeros bytes y buffer antes de reproducir.
      */
      if (video.readyState < 2) {
        try {
          video.load();
        } catch {}
      }
    };

    const resetVisualState = (
      rewind = true
    ) => {
      clearTimers();
      stopProgressTracking();

      videos.forEach((video) => {
        video.pause();

        if (rewind) {
          try {
            video.currentTime = 0;
          } catch {}
        }
      });

      activeVideo = currentVideo();

      completed = false;
      activeStep = -1;
      lastProgressValue = -1;

      dawn.classList.remove(
        'is-playing',
        'is-buffering',
        'is-complete',
        'is-autoplay-blocked',
        'is-looping'
      );

      setProgress(0, true);

      setReplayLabel('play');
    };

    const finish = () => {
      clearTimers();
      stopProgressTracking();

      started = true;
      completed = true;

      dawn.classList.remove(
        'is-playing',
        'is-buffering',
        'is-autoplay-blocked'
      );

      dawn.classList.add(
        'is-complete'
      );

      setProgress(1, true);

      setReplayLabel('replay');
    };

    const startFallback = () => {
      resetVisualState(false);

      started = true;

      dawn.classList.add(
        'is-playing'
      );

      let startedAt = null;

      const update = (timestamp) => {
        if (startedAt === null) {
          startedAt = timestamp;
        }

        const progress =
          (timestamp - startedAt) /
          fallbackDuration;

        setProgress(progress);

        if (progress >= 1) {
          fallbackFrame = 0;

          finish();

          return;
        }

        fallbackFrame =
          requestAnimationFrame(
            update
          );
      };

      fallbackFrame =
        requestAnimationFrame(
          update
        );
    };

    const startVideo = async () => {
      /*
        No ejecutamos reset() completo acá.

        El reset anterior forzaba:
        - pause
        - currentTime = 0
        - cálculo de layout
        - cambio de clases

        justo antes del play.

        Eso contribuía al tirón.
      */
      clearTimers();
      stopProgressTracking();

      const nextVideo =
        currentVideo();

      activeVideo =
        nextVideo;

      if (!activeVideo) {
        startFallback();
        return;
      }

      videos.forEach((video) => {
        if (video !== activeVideo) {
          video.pause();
        }
      });

      prewarmVideo(activeVideo);

      try {
        activeVideo.currentTime = 0;
      } catch {}

      activeStep = -1;
      lastProgressValue = -1;

      completed = false;
      started = true;

      setProgress(0, true);

      setReplayLabel('play');

      /*
        Todavía no decimos visualmente que está
        reproduciendo hasta que play() realmente
        haya sido resuelto.
      */
      dawn.classList.remove(
        'is-complete',
        'is-autoplay-blocked',
        'is-looping',
        'is-playing'
      );

      dawn.classList.add(
        'is-buffering'
      );

      try {
        await activeVideo.play();

        /*
          Si durante el await cambió el viewport
          o el breakpoint, evitamos dejar un video
          viejo reproduciéndose.
        */
        if (
          !isInViewport ||
          activeVideo !== nextVideo
        ) {
          nextVideo.pause();
          return;
        }

        dawn.classList.remove(
          'is-buffering'
        );

        dawn.classList.add(
          'is-playing'
        );

        trackVideoProgress(
          activeVideo
        );
      } catch {
        dawn.classList.remove(
          'is-playing',
          'is-buffering'
        );

        dawn.classList.add(
          'is-autoplay-blocked'
        );

        started = false;

        setReplayLabel('play');
      }
    };

    const stopOutOfView = () => {
      clearTimers();
      stopProgressTracking();

      videos.forEach((video) => {
        video.pause();

        try {
          video.currentTime = 0;
        } catch {}
      });

      started = false;
      completed = false;

      activeStep = -1;
      lastProgressValue = -1;

      dawn.classList.remove(
        'is-playing',
        'is-buffering',
        'is-complete',
        'is-autoplay-blocked',
        'is-looping'
      );

      setProgress(
        0,
        true
      );

      setReplayLabel(
        'play'
      );
    };

    const restartVideoLoop = (video) => {
      if (video !== activeVideo) return;

      stopProgressTracking();
      clearTimers();

      setProgress(1, true);

      if (!isInViewport) {
        stopOutOfView();
        return;
      }

      dawn.classList.add(
        'is-looping'
      );

      timers.push(
        window.setTimeout(
          async () => {
            if (
              !isInViewport ||
              video !== activeVideo
            ) {
              stopOutOfView();
              return;
            }

            try {
              video.currentTime = 0;
            } catch {}

            activeStep = -1;
            lastProgressValue = -1;

            completed = false;

            setProgress(
              0,
              true
            );

            dawn.classList.remove(
              'is-complete',
              'is-autoplay-blocked'
            );

            dawn.classList.add(
              'is-buffering'
            );

            try {
              await video.play();

              dawn.classList.remove(
                'is-buffering'
              );

              dawn.classList.add(
                'is-playing'
              );

              trackVideoProgress(
                video
              );
            } catch {
              dawn.classList.remove(
                'is-playing',
                'is-buffering',
                'is-looping'
              );

              dawn.classList.add(
                'is-autoplay-blocked'
              );

              started = false;

              setReplayLabel(
                'play'
              );
            }
          },
          360
        )
      );

      timers.push(
        window.setTimeout(
          () => {
            dawn.classList.remove(
              'is-looping'
            );
          },
          920
        )
      );
    };

    const play = () => {
      if (reduceMotion) {
        finish();
        return;
      }

      if (videos.length) {
        startVideo();
      } else {
        startFallback();
      }
    };

    videos.forEach((video) => {
      video.addEventListener(
        'waiting',
        () => {
          if (
            video === activeVideo &&
            started
          ) {
            dawn.classList.add(
              'is-buffering'
            );
          }
        }
      );

      video.addEventListener(
        'stalled',
        () => {
          if (
            video === activeVideo &&
            started
          ) {
            dawn.classList.add(
              'is-buffering'
            );
          }
        }
      );

      video.addEventListener(
        'playing',
        () => {
          if (
            video !== activeVideo ||
            !started
          ) {
            return;
          }

          dawn.classList.remove(
            'is-buffering'
          );

          dawn.classList.add(
            'is-playing'
          );
        }
      );

      video.addEventListener(
        'ended',
        () => {
          if (
            video === activeVideo
          ) {
            restartVideoLoop(
              video
            );
          }
        }
      );
    });

    replay?.addEventListener(
      'click',
      play
    );

    let resizeTimer = 0;

    window.addEventListener(
      'resize',
      () => {
        clearTimeout(
          resizeTimer
        );

        resizeTimer =
          window.setTimeout(
            () => {
              const nextVideo =
                currentVideo();

              if (
                nextVideo === activeVideo
              ) {
                return;
              }

              const shouldRestart =
                isInViewport &&
                started;

              stopOutOfView();

              activeVideo =
                nextVideo;

              prewarmVideo(
                nextVideo
              );

              if (shouldRestart) {
                startVideo();
              }
            },
            180
          );
      },
      {
        passive: true
      }
    );

    document.addEventListener(
      'visibilitychange',
      () => {
        if (
          !activeVideo ||
          !started
        ) {
          return;
        }

        if (document.hidden) {
          activeVideo.pause();

          stopProgressTracking();

          return;
        }

        if (
          isInViewport &&
          !completed
        ) {
          dawn.classList.add(
            'is-buffering'
          );

          activeVideo
            .play()
            .then(() => {
              dawn.classList.remove(
                'is-buffering'
              );

              dawn.classList.add(
                'is-playing'
              );

              trackVideoProgress(
                activeVideo
              );
            })
            .catch(() => {
              dawn.classList.remove(
                'is-buffering'
              );
            });
        }
      }
    );

    if (reduceMotion) {
      finish();

      replay?.setAttribute(
        'hidden',
        ''
      );

      return;
    }

    if (!autoplay) {
      dawn.classList.add(
        'is-autoplay-blocked'
      );

      setReplayLabel(
        'play'
      );
    }

    if (
      !('IntersectionObserver' in window)
    ) {
      isInViewport = true;

      prewarmVideo();

      if (autoplay) {
        play();
      }

      return;
    }

    /*
      El video empieza a prepararse bastante antes
      de que el usuario llegue visualmente al bloque.

      Esto ataca el principal micro-freeze:
      antes se pedía el video al mismo tiempo que
      se intentaba reproducir.
    */
    const prewarmObserver =
      new IntersectionObserver(
        (entries) => {
          entries.forEach(
            (entry) => {
              if (!entry.isIntersecting) {
                return;
              }

              prewarmVideo();

              prewarmObserver.unobserve(
                dawn
              );
            }
          );
        },
        {
          rootMargin:
            '900px 0px 900px 0px',

          threshold: 0
        }
      );

    prewarmObserver.observe(
      dawn
    );

    /*
      Este observer decide únicamente
      cuándo empieza la experiencia.

      La descarga ya comenzó antes.
    */
    const observer =
      new IntersectionObserver(
        (entries) => {
          entries.forEach(
            (entry) => {
              const inViewportNow =
                entry.isIntersecting &&
                entry.intersectionRatio > 0;

              if (!inViewportNow) {
                if (isInViewport) {
                  stopOutOfView();
                }

                isInViewport = false;
                reachedStartThreshold = false;

                return;
              }

              isInViewport = true;

              const readyToStart =
                entry.intersectionRatio >=
                threshold;

              if (
                autoplay &&
                readyToStart &&
                !reachedStartThreshold
              ) {
                reachedStartThreshold = true;

                play();
              }
            }
          );
        },
        {
          threshold: [
            0,
            threshold,
            1
          ]
        }
      );

    observer.observe(
      dawn
    );
  };

  const initSunrise = (root) => {
    const demo = root.querySelector('[data-sunrise-demo]');
    if (!demo) return;
    const sky = demo.querySelector('.bmh-sunrise-sky');
    const buttons = [...demo.querySelectorAll('[data-sunrise-step]')];
    const time = demo.querySelector('[data-sunrise-time]');
    const label = demo.querySelector('[data-sunrise-label]');
    const play = demo.querySelector('[data-sunrise-play]');
    const playLabel = demo.querySelector('[data-sunrise-play-label]');
    let timers = [];
    let playing = false;
    const states = [
      { progress: 0.08, time: '10 minutos antes', clock: '06:50', label: 'Empieza una luz cálida y tenue.' },
      { progress: 0.38, time: 'Durante el amanecer', clock: '06:53', label: 'La intensidad sube al segundo nivel.' },
      { progress: 0.72, time: 'Cerca de la hora', clock: '06:57', label: 'Llega al tercer nivel de luz.' },
      { progress: 1, time: 'A la hora programada', clock: '07:00', label: 'Empieza el sonido y su volumen aumenta de a poco.' }
    ];

    const stopTimers = () => {
      timers.forEach(clearTimeout);
      timers = [];
      playing = false;
      if (playLabel) playLabel.textContent = 'Ver los 10 minutos en 8 segundos';
    };

    const setStep = (index) => {
      const state = states[index] || states[0];
      sky?.style.setProperty('--sunrise-progress', state.progress);
      const clock = demo.querySelector('.bmh-sunrise-clock');
      if (clock) clock.textContent = state.clock;
      if (time) time.textContent = state.time;
      if (label) label.textContent = state.label;
      buttons.forEach((button, buttonIndex) => {
        const active = buttonIndex === index;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        stopTimers();
        setStep(Number(button.dataset.sunriseStep));
      });
    });

    play?.addEventListener('click', () => {
      if (playing) {
        stopTimers();
        return;
      }
      stopTimers();
      playing = true;
      if (playLabel) playLabel.textContent = 'Pausar demostración';
      setStep(0);
      [1, 2, 3].forEach((step, index) => {
        timers.push(setTimeout(() => setStep(step), (index + 1) * 2000));
      });
      timers.push(setTimeout(() => {
        playing = false;
        if (playLabel) playLabel.textContent = 'Repetir demostración';
      }, 8200));
    });

    setStep(0);
  };

  const initRoutine = (root) => {
    const routine = root.querySelector('[data-routine]');
    if (!routine) return;
    const tabs = [...routine.querySelectorAll('[data-routine-tab]')];
    const panels = [...routine.querySelectorAll('[data-routine-panel]')];

    const ring = routine.querySelector('[data-breathing-ring]');
    const toggle = routine.querySelector('[data-breathing-toggle]');
    const phase = routine.querySelector('[data-breathing-phase]');
    const kicker = routine.querySelector('[data-breathing-kicker]');
    const startLabel = toggle?.textContent.trim() || 'Ver demostración';
    const idleLabel = phase?.textContent.trim() || 'Probá el ritmo';
    const instructionLabel = kicker?.textContent.trim() || 'Mantené presionado';
    let holdTimer;
    let phaseTimer;
    let startedFromHold = false;
    let breathing = false;
    let inhale = true;

    const renderBreathing = () => {
      ring?.classList.toggle('is-breathing', breathing);
      if (toggle) toggle.textContent = breathing ? 'Detener demostración' : startLabel;
      if (kicker) kicker.textContent = breathing ? 'Acompañá el ritmo' : instructionLabel;
      if (phase) phase.textContent = breathing ? (inhale ? 'Inhalá' : 'Exhalá') : idleLabel;
    };

    const startBreathing = () => {
      if (breathing) return;
      breathing = true;
      inhale = true;
      renderBreathing();
      clearInterval(phaseTimer);
      phaseTimer = setInterval(() => {
        inhale = !inhale;
        if (phase) phase.textContent = inhale ? 'Inhalá' : 'Exhalá';
      }, 4000);
    };

    const stopBreathing = () => {
      breathing = false;
      clearInterval(phaseTimer);
      renderBreathing();
    };

    const activateTab = (tab, moveFocus = false) => {
      if (!tab) return;
      const selected = tab.dataset.routineTab;
      tabs.forEach((item) => {
        const active = item === tab;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        const active = panel.dataset.routinePanel === selected;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      if (selected !== 'breathe') stopBreathing();
      if (moveFocus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activateTab(tab));
      tab.addEventListener('keydown', (event) => {
        let nextIndex = index;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = tabs.length - 1;
        else return;
        event.preventDefault();
        activateTab(tabs[nextIndex], true);
      });
    });

    const holdStart = (event) => {
      if (breathing || event.button > 0) return;
      startedFromHold = false;
      ring?.classList.add('is-holding');
      holdTimer = setTimeout(() => {
        startedFromHold = true;
        startBreathing();
      }, 500);
    };

    const holdEnd = () => {
      clearTimeout(holdTimer);
      ring?.classList.remove('is-holding');
    };

    ring?.addEventListener('pointerdown', holdStart);
    ring?.addEventListener('pointerup', holdEnd);
    ring?.addEventListener('pointercancel', holdEnd);
    ring?.addEventListener('pointerleave', holdEnd);
    ring?.addEventListener('click', () => {
      if (startedFromHold) {
        startedFromHold = false;
        return;
      }
      breathing ? stopBreathing() : startBreathing();
    });
    toggle?.addEventListener('click', () => breathing ? stopBreathing() : startBreathing());
    renderBreathing();
  };

  const trackInteraction = (name, detail = {}) => {
    const payload = { component: 'sunrise_product_page', ...detail };
    document.dispatchEvent(new CustomEvent('bmh:interaction', { detail: { name, ...payload } }));
    if (typeof window.fbq === 'function') window.fbq('trackCustom', name, payload);
  };

  const initJourney = (root) => {
    const story = root.querySelector('[data-bmh-journey]');
    if (!story || story.dataset.bmhJourneyInitialized === 'true') return;
    story.dataset.bmhJourneyInitialized = 'true';

    const chapters = [...story.querySelectorAll('[data-routine-chapter]')];
    if (!chapters.length) return;

    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const ratios = new Map(chapters.map((chapter) => [chapter, 0]));
    const tracked = new WeakSet();
    let activeChapter = null;
    let resizeTimer = 0;

    story.classList.add('is-enhanced');

    const currentVideo = (chapter) => {
      const videos = [...chapter.querySelectorAll('video')];
      return videos.find((video) => getComputedStyle(video).display !== 'none') || videos[0] || null;
    };

    const pauseChapter = (chapter, reset = false) => {
      chapter.querySelectorAll('video').forEach((video) => {
        video.pause();
        if (reset) {
          try { video.currentTime = 0; } catch {}
        }
      });
      if (reset) chapter.classList.remove('is-video-playing');
    };

    const playChapter = async (chapter) => {
      if (!chapter || reducedMotion.matches || document.hidden) return;
      const video = currentVideo(chapter);
      if (!video) return;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      try {
        await video.play();
        if (chapter === activeChapter) chapter.classList.add('is-video-playing');
      } catch {
        chapter.classList.remove('is-video-playing');
      }
    };

    const activate = (chapter) => {
      if (chapter === activeChapter) {
        if (chapter) playChapter(chapter);
        return;
      }

      chapters.forEach((item) => {
        const selected = item === chapter;
        item.classList.toggle('is-active', selected);
        if (!selected) pauseChapter(item);
      });

      activeChapter = chapter || null;
      if (!activeChapter) {
        story.style.setProperty('--bmh-routine-progress', '0');
        return;
      }

      const index = chapters.indexOf(activeChapter);
      story.style.setProperty('--bmh-routine-progress', String(Math.max(0, index)));
      activeChapter.classList.add('is-seen');
      playChapter(activeChapter);

      if (!tracked.has(activeChapter)) {
        tracked.add(activeChapter);
        trackInteraction('BMH_RoutineSceneViewed', {
          scene: activeChapter.dataset.routineKind || String(index + 1),
          position: index + 1
        });
      }
    };

    chapters.forEach((chapter) => {
      chapter.querySelectorAll('video').forEach((video) => {
        video.addEventListener('playing', () => {
          if (chapter === activeChapter && video === currentVideo(chapter)) {
            chapter.classList.add('is-video-playing');
          }
        });
        video.addEventListener('pause', () => {
          if (video.currentTime === 0 || video.ended) chapter.classList.remove('is-video-playing');
        });
      });
    });

    if (reducedMotion.matches) {
      chapters.forEach((chapter) => chapter.classList.add('is-seen'));
      chapters[0].classList.add('is-active');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      chapters.forEach((chapter) => chapter.classList.add('is-seen'));
      activate(chapters[0]);
      return;
    }

    const chooseActive = () => {
      let bestChapter = null;
      let bestRatio = 0;
      chapters.forEach((chapter) => {
        const ratio = ratios.get(chapter) || 0;
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestChapter = chapter;
        }
      });
      activate(bestRatio >= .42 ? bestChapter : null);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
        if (entry.isIntersecting && entry.intersectionRatio >= .14) {
          entry.target.classList.add('is-seen');
        }
        if (!entry.isIntersecting || entry.intersectionRatio === 0) {
          pauseChapter(entry.target, true);
        }
      });
      chooseActive();
    }, { threshold: [0, .14, .28, .42, .58, .76] });

    chapters.forEach((chapter) => observer.observe(chapter));

    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (!activeChapter) return;
        pauseChapter(activeChapter);
        playChapter(activeChapter);
      }, 180);
    }, { passive: true });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        chapters.forEach((chapter) => pauseChapter(chapter));
      } else if (activeChapter) {
        playChapter(activeChapter);
      }
    });
  };

  const initDayflow = (root) => {
    const story = root.matches?.('[data-bmh-dayflow]')
      ? root
      : root.querySelector?.('[data-bmh-dayflow]');

    if (!story || story.dataset.bmhDayflowInitialized === 'true') return;
    story.dataset.bmhDayflowInitialized = 'true';

    const scenes = [...story.querySelectorAll('[data-dayflow-scene]')];
    const railButtons = [...story.querySelectorAll('[data-dayflow-jump]')];
    const mobileButtons = [...story.querySelectorAll('[data-dayflow-mobile-jump]')];
    const mobileCurrent = story.querySelector('[data-dayflow-mobile-current]');
    const autoplayToggle = story.querySelector('[data-dayflow-autoplay-toggle]');
    const track = story.querySelector('[data-dayflow-track]');
    if (!scenes.length) return;

    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const mobileLayout = matchMedia('(max-width: 899px)');
    const autoplayEnabled = story.dataset.dayflowAutoplay !== 'false';
    const autoplayDelay = Math.max(4000, Math.min(8000, (Number(story.dataset.dayflowDelay) || 5) * 1000));
    const visibleScenes = new Set();
    const tracked = new WeakSet();
    const parallaxStrength = Math.max(
      0,
      Math.min(36, Number(getComputedStyle(story.closest('.bmh-dayflow-page') || story)
        .getPropertyValue('--dayflow-parallax')) || 20)
    );

    let activeScene = null;
    let rafId = 0;
    let resizeTimer = 0;
    let autoplayTimer = 0;
    let autoplayResumeTimer = 0;
    let autoplayDirection = 1;
    let autoplayPausedByUser = false;
    let storyVisible = false;

    story.classList.add('is-enhanced');

    story.addEventListener('dragstart', (event) => {
      if (event.target.closest('img, video, picture')) event.preventDefault();
    });

    const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

    const currentVideo = (scene) => {
      const videos = [...scene.querySelectorAll('video')];
      return videos.find((video) => getComputedStyle(video).display !== 'none') || videos[0] || null;
    };

    const pauseSceneVideo = (scene, reset = false) => {
      scene.querySelectorAll('video').forEach((video) => {
        video.pause();
        if (reset) {
          try { video.currentTime = 0; } catch {}
        }
      });
      if (reset) scene.classList.remove('is-video-playing');
    };

    const playSceneVideo = async (scene) => {
      if (!scene || reducedMotion.matches || document.hidden || !visibleScenes.has(scene)) return;
      const video = currentVideo(scene);
      if (!video) return;

      scenes.forEach((other) => {
        if (other !== scene) pauseSceneVideo(other);
      });

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      try {
        await video.play();
        if (scene === activeScene) scene.classList.add('is-video-playing');
      } catch {
        scene.classList.remove('is-video-playing');
      }
    };

    const autoplayCanRun = () => (
      autoplayEnabled &&
      mobileLayout.matches &&
      storyVisible &&
      !document.hidden &&
      !reducedMotion.matches &&
      !autoplayPausedByUser
    );

    const updateAutoplayUI = () => {
      const running = Boolean(autoplayTimer) && autoplayCanRun();
      story.classList.toggle('is-autoplaying', running);
      story.classList.toggle('is-autoplay-paused', autoplayEnabled && !running);

      if (!autoplayToggle) return;
      autoplayToggle.hidden = !autoplayEnabled;
      autoplayToggle.dataset.state = running ? 'playing' : 'paused';
      autoplayToggle.setAttribute('aria-pressed', String(!running));
      autoplayToggle.setAttribute(
        'aria-label',
        running ? 'Pausar carrusel automático' : 'Reanudar carrusel automático'
      );
    };

    const clearAutoplay = () => {
      if (autoplayTimer) window.clearTimeout(autoplayTimer);
      autoplayTimer = 0;
      updateAutoplayUI();
    };

    const scrollToScene = (scene, behaviorOverride) => {
      if (!scene) return;
      const behavior = behaviorOverride || (reducedMotion.matches ? 'auto' : 'smooth');

      if (mobileLayout.matches && track) {
        const targetLeft = scene.offsetLeft - ((track.clientWidth - scene.offsetWidth) / 2);
        track.scrollTo({ left: Math.max(0, targetLeft), behavior });
        return;
      }

      scene.scrollIntoView({
        behavior,
        block: 'center',
        inline: 'nearest'
      });
    };

    const armAutoplay = () => {
      if (autoplayTimer) window.clearTimeout(autoplayTimer);
      autoplayTimer = 0;

      if (!autoplayCanRun()) {
        updateAutoplayUI();
        return;
      }

      autoplayTimer = window.setTimeout(() => {
        autoplayTimer = 0;
        const index = Math.max(0, scenes.indexOf(activeScene || scenes[0]));

        if (index >= scenes.length - 1) autoplayDirection = -1;
        else if (index <= 0) autoplayDirection = 1;

        const nextIndex = clamp(index + autoplayDirection, 0, scenes.length - 1);
        scrollToScene(scenes[nextIndex], 'smooth');

        window.setTimeout(() => {
          if (!autoplayTimer && autoplayCanRun()) armAutoplay();
        }, 900);
      }, autoplayDelay);

      updateAutoplayUI();
    };

    const pauseAutoplayTemporarily = () => {
      clearAutoplay();
      clearTimeout(autoplayResumeTimer);
      autoplayResumeTimer = window.setTimeout(() => {
        autoplayResumeTimer = 0;
        if (autoplayCanRun()) armAutoplay();
      }, autoplayDelay);
    };

    const pauseAutoplayByUser = () => {
      autoplayPausedByUser = true;
      clearTimeout(autoplayResumeTimer);
      clearAutoplay();
    };

    const syncNavigation = (scene) => {
      const index = Math.max(0, scenes.indexOf(scene));

      scenes.forEach((item) => {
        item.classList.toggle('is-active', item === scene);
      });

      railButtons.forEach((button, buttonIndex) => {
        button.classList.toggle('is-active', buttonIndex === index);
      });

      mobileButtons.forEach((button, buttonIndex) => {
        const current = buttonIndex === index;
        button.setAttribute('aria-current', String(current));
        button.tabIndex = current ? 0 : -1;
      });

      if (mobileCurrent) {
        mobileCurrent.textContent = String(index + 1).padStart(2, '0');
      }
    };

    const syncActiveState = (scene, force = false) => {
      if (!scene) return;
      const changed = scene !== activeScene;
      activeScene = scene;
      syncNavigation(scene);

      if ((changed || force) && visibleScenes.has(scene)) {
        playSceneVideo(scene);
      }

      if (changed && autoplayCanRun()) armAutoplay();

      if (!tracked.has(scene) && visibleScenes.has(scene)) {
        tracked.add(scene);
        const index = scenes.indexOf(scene);
        trackInteraction('BMH_RoutineSceneViewed', {
          scene: scene.dataset.dayflowKind || String(index + 1),
          position: index + 1
        });
      }
    };

    const setWakeProgress = (scene, progress) => {
      if (scene.dataset.dayflowKind !== 'wake') return;
      const glow = clamp(progress);
      const brightness = .80 + glow * .24;

      scene.style.setProperty('--wake-glow', glow.toFixed(3));
      scene.style.setProperty('--wake-brightness', brightness.toFixed(3));
      scene.style.setProperty('--wake-one', (0.24 + clamp((glow - .18) / .18) * .76).toFixed(3));
      scene.style.setProperty('--wake-two', (0.24 + clamp((glow - .34) / .18) * .76).toFixed(3));
      scene.style.setProperty('--wake-three', (0.24 + clamp((glow - .50) / .18) * .76).toFixed(3));
    };

    const renderScene = (scene, viewportHeight, mobileCenter) => {
      const rect = scene.getBoundingClientRect();

      if (mobileLayout.matches) {
        const center = rect.left + rect.width / 2;
        const signedOffset = clamp(
          (center - mobileCenter) / Math.max(1, rect.width + 12),
          -1.35,
          1.35
        );
        const distance = Math.abs(center - mobileCenter);
        const focus = clamp(1 - Math.abs(signedOffset));
        const easedFocus = 1 - Math.pow(1 - focus, 2);

        scene.style.setProperty('--scene-progress', easedFocus.toFixed(4));
        scene.style.setProperty('--scene-parallax', '0px');
        scene.style.setProperty('--slide-scale', (.82 + easedFocus * .18).toFixed(4));
        scene.style.setProperty('--slide-depth', (-86 + easedFocus * 86).toFixed(2) + 'px');
        scene.style.setProperty('--slide-y', ((1 - easedFocus) * 10).toFixed(2) + 'px');
        scene.style.setProperty('--slide-opacity', (.46 + easedFocus * .54).toFixed(4));
        scene.style.setProperty('--product-scale', (.90 + easedFocus * .10).toFixed(4));
        scene.style.setProperty('--slide-shift', (-signedOffset * 12).toFixed(2) + 'px');
        scene.style.setProperty('--slide-tilt', (-signedOffset * 2.8).toFixed(2) + 'deg');
        scene.style.setProperty('--slide-media-shift', (-signedOffset * 9).toFixed(2) + 'px');
        scene.style.setProperty('--copy-y', ((1 - easedFocus) * 15).toFixed(2) + 'px');
        scene.style.setProperty('--copy-opacity', (.18 + easedFocus * .82).toFixed(4));
        setWakeProgress(scene, .16 + easedFocus * .84);
        return distance;
      }

      const progress = clamp(
        (viewportHeight - rect.top) / Math.max(1, viewportHeight + rect.height)
      );
      const centered = (progress - .5) * 2;
      const parallax = reducedMotion.matches ? 0 : -centered * parallaxStrength;

      scene.style.setProperty('--scene-progress', progress.toFixed(4));
      scene.style.setProperty('--scene-parallax', parallax.toFixed(2) + 'px');
      setWakeProgress(scene, clamp((progress - .08) / .72));

      return Math.abs((rect.top + rect.height / 2) - viewportHeight / 2);
    };

    const update = () => {
      rafId = 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const trackRect = track?.getBoundingClientRect();
      const mobileCenter = trackRect
        ? trackRect.left + trackRect.width / 2
        : (window.innerWidth || document.documentElement.clientWidth) / 2;
      let closest = null;
      let closestDistance = Infinity;
      const candidates = visibleScenes.size ? [...visibleScenes] : scenes;

      candidates.forEach((scene) => {
        const distance = renderScene(scene, viewportHeight, mobileCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closest = scene;
        }
      });

      if (closest) syncActiveState(closest);
    };

    const requestUpdate = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(update);
    };

    railButtons.forEach((button, index) => {
      button.addEventListener('click', () => scrollToScene(scenes[index]));
    });

    mobileButtons.forEach((button, index) => {
      button.addEventListener('click', () => {
        pauseAutoplayTemporarily();
        scrollToScene(scenes[index]);
      });
    });

    autoplayToggle?.addEventListener('click', () => {
      if (autoplayTimer || !autoplayPausedByUser) {
        pauseAutoplayByUser();
      } else {
        autoplayPausedByUser = false;
        armAutoplay();
      }
    });

    track?.addEventListener('pointerdown', pauseAutoplayTemporarily, { passive: true });
    track?.addEventListener('focusin', pauseAutoplayTemporarily);

    scenes.forEach((scene) => {
      scene.querySelectorAll('video').forEach((video) => {
        video.addEventListener('playing', () => {
          if (scene === activeScene && video === currentVideo(scene)) {
            scene.classList.add('is-video-playing');
          }
        });

        video.addEventListener('pause', () => {
          if (video.currentTime === 0 || video.ended) {
            scene.classList.remove('is-video-playing');
          }
        });
      });
    });

    if ('IntersectionObserver' in window) {
      const sceneObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const scene = entry.target;

          if (entry.isIntersecting) {
            visibleScenes.add(scene);
            if (entry.intersectionRatio >= .08) scene.classList.add('is-seen');
          } else {
            visibleScenes.delete(scene);
            pauseSceneVideo(scene, true);
          }
        });
        requestUpdate();
      }, {
        rootMargin: '10% 0px 10% 0px',
        threshold: [0, .08, .18, .35, .55, .75]
      });

      scenes.forEach((scene) => sceneObserver.observe(scene));

      const storyObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target !== story) return;
          storyVisible = entry.isIntersecting && entry.intersectionRatio >= .28;
          if (storyVisible) armAutoplay();
          else clearAutoplay();
        });
      }, { threshold: [0, .28, .55] });

      storyObserver.observe(story);
    } else {
      storyVisible = true;
      scenes.forEach((scene) => {
        scene.classList.add('is-seen');
        visibleScenes.add(scene);
      });
    }

    if (reducedMotion.matches) {
      scenes.forEach((scene) => {
        scene.classList.add('is-seen');
        scene.style.setProperty('--scene-parallax', '0px');
        if (scene.dataset.dayflowKind === 'wake') setWakeProgress(scene, 1);
      });
    }

    window.addEventListener('scroll', requestUpdate, { passive: true });
    track?.addEventListener('scroll', requestUpdate, { passive: true });

    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        requestUpdate();
        if (activeScene) {
          pauseSceneVideo(activeScene);
          playSceneVideo(activeScene);
        }
        armAutoplay();
      }, 160);
    }, { passive: true });

    mobileLayout.addEventListener?.('change', () => {
      scenes.forEach((scene) => pauseSceneVideo(scene, true));
      clearAutoplay();
      window.setTimeout(() => {
        scrollToScene(activeScene || scenes[0], 'auto');
        requestUpdate();
        armAutoplay();
      }, 80);
    });

    reducedMotion.addEventListener?.('change', () => {
      clearAutoplay();
      requestUpdate();
      armAutoplay();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        scenes.forEach((scene) => pauseSceneVideo(scene));
        clearAutoplay();
      } else {
        playSceneVideo(activeScene);
        requestUpdate();
        armAutoplay();
      }
    });

    syncActiveState(scenes[0], true);
    requestUpdate();
    updateAutoplayUI();
    if (storyVisible) armAutoplay();
  };

  const initAudio = (root) => {
    const player = root.querySelector('[data-audio-player]');
    if (!player) return;
    const audio = player.querySelector('[data-audio-element]');
    const title = player.querySelector('[data-audio-title]');
    const toggle = player.querySelector('[data-audio-toggle]');
    const progress = player.querySelector('[data-audio-progress]');
    const current = player.querySelector('[data-audio-current]');
    const duration = player.querySelector('[data-audio-duration]');
    const sources = [...root.querySelectorAll('[data-audio-source]')];
    let activeButton = null;

    const render = () => {
      const playing = !audio.paused && !audio.ended;
      player.classList.toggle('is-playing', playing);
      if (toggle) toggle.textContent = playing ? 'Ⅱ' : '▶';
      if (toggle) toggle.setAttribute('aria-label', playing ? 'Pausar' : 'Reproducir');
    };

    const select = async (button, autoplay = true) => {
      if (!button) return;
      const source = button.dataset.audioSource;
      if (!source) return;
      if (activeButton !== button) {
        activeButton?.classList.remove('is-active');
        activeButton = button;
        activeButton.classList.add('is-active');
        audio.src = source;
        audio.load();
        if (title) title.textContent = button.dataset.audioName || 'Sonido relajante';
      }
      if (autoplay) {
        try { await audio.play(); } catch { render(); }
      }
    };

    sources.forEach((button) => button.addEventListener('click', () => {
      if (activeButton === button && !audio.paused) audio.pause();
      else select(button);
    }));

    toggle?.addEventListener('click', () => {
      if (!activeButton) {
        select(sources[0]);
        return;
      }
      audio.paused ? audio.play().catch(render) : audio.pause();
    });

    audio.addEventListener('play', render);
    audio.addEventListener('pause', render);
    audio.addEventListener('ended', render);
    audio.addEventListener('loadedmetadata', () => {
      if (duration) duration.textContent = formatTime(audio.duration);
    });
    audio.addEventListener('timeupdate', () => {
      if (current) current.textContent = formatTime(audio.currentTime);
      if (progress && Number.isFinite(audio.duration) && audio.duration > 0) {
        progress.value = String((audio.currentTime / audio.duration) * 100);
      }
    });
    progress?.addEventListener('input', () => {
      if (Number.isFinite(audio.duration)) audio.currentTime = (Number(progress.value) / 100) * audio.duration;
    });
    render();
  };

  const initDates = (root) => {
    const formatter = new Intl.DateTimeFormat('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
    root.querySelectorAll('[data-relative-date]').forEach((element) => {
      const parsed = new Date(element.dataset.relativeDate + 'T12:00:00');
      if (Number.isNaN(parsed.getTime())) return;
      const days = Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 86400000));
      let text = 'Hoy';
      if (days === 1) text = 'Ayer';
      else if (days < 30) text = 'Hace ' + days + ' días';
      else if (days < 365) {
        const months = Math.max(1, Math.floor(days / 30.44));
        text = 'Hace ' + months + ' ' + (months === 1 ? 'mes' : 'meses');
      } else {
        const years = Math.max(1, Math.floor(days / 365.25));
        text = 'Hace ' + years + ' ' + (years === 1 ? 'año' : 'años');
      }
      element.textContent = text;
      element.title = formatter.format(parsed);
      element.setAttribute('aria-label', formatter.format(parsed));
    });
  };

  const initReviews = (root) => {
    const list = root.querySelector('[data-review-list]');
    const toggle = root.querySelector('[data-reviews-toggle]');
    if (!list || !toggle) return;
    toggle.addEventListener('click', () => {
      const expanded = list.classList.toggle('is-expanded');
      toggle.textContent = expanded ? 'Ver menos opiniones' : 'Ver más opiniones';
      toggle.setAttribute('aria-expanded', String(expanded));
    });
  };

  const initComparison = (root) => {
    const compare = root.querySelector('[data-compare]');
    if (!compare) return;
    const buttons = [...compare.querySelectorAll('[data-compare-mode]')];
    const stage = compare.querySelector('[data-compare-stage]');
    const summary = compare.querySelector('[data-compare-summary]');
    const values = [...compare.querySelectorAll('dd[data-phone][data-bmh]')];
    const copy = {
      phone: 'La alarma empieza de golpe a la hora elegida y el celular queda al alcance de la mano.',
      bmh: 'La luz empieza 10 minutos antes y el sonido llega después.'
    };
    const setMode = (mode) => {
      buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.compareMode === mode)));
      if (stage) stage.dataset.compareStage = mode;
      if (summary) summary.textContent = copy[mode];
      values.forEach((value) => { value.textContent = value.dataset[mode] || ''; });
    };
    buttons.forEach((button) => button.addEventListener('click', () => setMode(button.dataset.compareMode)));
    setMode('phone');
  };

  const initTutorial = (root) => {
    const facade = root.querySelector('[data-youtube-facade]');
    if (!facade) return;
    const id = youtubeId(facade.dataset.youtubeUrl || '');
    if (!id) return;
    const load = (start = 0) => {
      const frame = document.createElement('iframe');
      frame.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0&enablejsapi=1&start=' + Math.max(0, start);
      frame.title = 'Tutorial del Despertador Amanecer BeMyHome';
      frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      frame.allowFullscreen = true;
      facade.replaceChildren(frame);
      facade.classList.add('is-loaded');
    };
    facade.querySelector('[data-youtube-play]')?.addEventListener('click', () => load(0));
    root.querySelectorAll('[data-video-time]').forEach((button) => {
      button.addEventListener('click', () => load(timecodeToSeconds(button.dataset.videoTime)));
    });
  };

  const initStickyAndCart = () => {
    const sticky = document.querySelector('[data-bmh-sticky]');
    const hero = document.querySelector('#MainContent[data-template="product.oferta-limitada"] .product-information');
    const nativeAdd = document.querySelector('#MainContent[data-template="product.oferta-limitada"] product-form-component button[name="add"], #MainContent[data-template="product.oferta-limitada"] product-form-component [ref="addToCartButton"]');
    if (!sticky || sticky.dataset.bmhStickyInitialized === 'true') return;
    sticky.dataset.bmhStickyInitialized = 'true';

    /* Fixed UI inside a Shopify section can be clipped or deferred on mobile. */
    if (sticky.parentElement !== document.body) document.body.appendChild(sticky);

    const isRendered = (element) => {
      if (!element) return false;
      const styles = getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      return styles.display !== 'none' && styles.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
    };
    const findPrimaryCta = () => {
      const candidates = [
        hero?.querySelector('.ai-premium-purchase__add-to-cart'),
        hero?.querySelector('[data-testid="standalone-add-to-cart"]'),
        hero?.querySelector('button[name="add"]')
      ].filter(Boolean);
      return candidates.find(isRendered) || candidates[0] || nativeAdd;
    };

    const setSticky = (visible) => {
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      document.body.classList.toggle('bmh-sticky-visible', visible);
    };

    let primaryCta = findPrimaryCta();
    let stickyFrame = 0;
    const updateStickyPosition = () => {
      stickyFrame = 0;
      if (!isRendered(primaryCta)) primaryCta = findPrimaryCta();
      if (!primaryCta) {
        setSticky(Boolean(hero && hero.getBoundingClientRect().bottom < 0));
        return;
      }
      const rect = primaryCta.getBoundingClientRect();
      const header = document.querySelector('header[role="banner"], header-component, .header-section');
      const headerRect = header?.getBoundingClientRect();
      const headerBottom = headerRect && headerRect.bottom > 0 && headerRect.top <= 0 ? headerRect.bottom : 0;
      setSticky(rect.bottom <= headerBottom);
    };
    const requestStickyUpdate = () => {
      if (stickyFrame) return;
      stickyFrame = requestAnimationFrame(updateStickyPosition);
    };

    window.addEventListener('scroll', requestStickyUpdate, { passive: true });
    document.addEventListener('scroll', requestStickyUpdate, { passive: true, capture: true });
    window.addEventListener('resize', requestStickyUpdate, { passive: true });
    window.addEventListener('pageshow', requestStickyUpdate, { passive: true });
    if (primaryCta && 'ResizeObserver' in window) new ResizeObserver(requestStickyUpdate).observe(primaryCta);
    if (primaryCta && 'IntersectionObserver' in window) {
      new IntersectionObserver(requestStickyUpdate, { threshold: [0, 1] }).observe(primaryCta);
    }
    requestStickyUpdate();
    window.setTimeout(requestStickyUpdate, 250);
    window.setTimeout(requestStickyUpdate, 900);

    document.querySelectorAll('[data-bmh-add]').forEach((button) => {
      button.addEventListener('click', () => {
        if (nativeAdd && !nativeAdd.disabled) {
          nativeAdd.click();
          return;
        }
        document.querySelector('#MainContent[data-template="product.oferta-limitada"] .product-information')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    const syncCart = () => {
      const openDialog = document.querySelector('cart-drawer-component dialog[open], cart-drawer-component [open]');
      document.body.classList.toggle('bmh-cart-open', Boolean(openDialog));
    };
    const cart = document.querySelector('cart-drawer-component');
    if (cart) {
      new MutationObserver(syncCart).observe(cart, { attributes: true, subtree: true, attributeFilter: ['open'] });
      syncCart();
    }
  };

  const initPage = (root) => {
    if (root.dataset.bmhInitialized === 'true') return;
    root.dataset.bmhInitialized = 'true';
    initReveal(root);
    initDawnStory(root);
    initSunrise(root);
    initRoutine(root);
    initJourney(root);
    initDayflow(root);
    initAudio(root);
    initDates(root);
    initReviews(root);
    initComparison(root);
    initTutorial(root);
    initStickyAndCart();
  };

  const initAll = (scope = document) => {
    initHero(scope);
    scope.querySelectorAll(SELECTOR).forEach(initPage);
    initStickyAndCart();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
